import { Link } from 'react-router-dom'
import { CheckCircle2, ArrowRight } from 'lucide-react'
import SEO from '../../components/SEO'
import Footer from '../../components/Footer'

export default function PocketBaseComparison() {
  const siteUrl = 'https://solarch.in'
  const pagePath = '/comparisons/pocketbase'

  const breadcrumbsSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Comparisons",
        "item": `${siteUrl}/comparisons/pocketbase`,
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Solarch vs. PocketBase",
        "item": `${siteUrl}${pagePath}`,
      },
    ],
  }

  const techArticleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "Solarch vs. PocketBase: TypeScript-Native BaaS Comparison",
    "description":
      "An objective, technical comparison between Solarch and PocketBase: runtime environments, realtime architectures (WebSocket + SSE vs. SSE), SQLite data layers, extension models, and deployment trade-offs.",
    "url": `${siteUrl}${pagePath}`,
    "inLanguage": "en-US",
    "about": {
      "@id": "https://solarch.in/#software",
    },
    "author": {
      "@id": "https://solarch.in/#organization",
    },
    "publisher": {
      "@id": "https://solarch.in/#organization",
    },
  }

  return (
    <>
      <SEO
        title="Solarch vs. PocketBase: TypeScript-Native BaaS Comparison"
        description="An objective, technical comparison between Solarch and PocketBase: runtime environments, realtime architectures (WebSocket + SSE vs. SSE), SQLite data layers, extension models, and deployment trade-offs."
        canonical={pagePath}
        jsonLd={[breadcrumbsSchema, techArticleSchema]}
      />

      <div className="min-h-screen bg-theme-body pt-14 flex flex-col">
        {/* Header Section */}
        <header className="relative border-b border-theme bg-theme-surface/30 py-12 sm:py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-theme bg-theme-surface px-3 py-1 text-xs font-medium text-primary">
              Architectural Analysis
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-theme sm:text-4xl lg:text-5xl">
              Solarch vs. PocketBase: TypeScript-Native BaaS Comparison
            </h1>
            <p className="mt-4 text-base leading-relaxed text-theme-secondary sm:text-lg">
              An architectural and operational comparison for developers evaluating lightweight,
              embedded-database Backend-as-a-Service platforms.
            </p>
          </div>
        </header>

        {/* Content Section */}
        <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8 flex-1 space-y-12">
          {/* Neutral Introduction */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Introduction
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              Both Solarch and PocketBase address the same core challenge: eliminating the infrastructure
              overhead of traditional multi-tier backends by packaging an embedded database, authentication,
              file storage, real-time events, and an administrative dashboard into a single unit. Both
              platforms leverage embedded SQLite with Write-Ahead Logging (WAL) rather than requiring external
              database servers like PostgreSQL or MySQL.
            </p>
            <p className="text-base leading-relaxed text-theme-secondary">
              However, the two projects make fundamentally different architectural choices in their server
              runtime, extension model, realtime protocols, and ecosystem integration. This guide outlines
              how those choices impact development, customization, and deployment so you can select the
              right tool for your stack.
            </p>
          </section>

          {/* Quick Comparison Matrix */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Quick Comparison Matrix
            </h2>
            <div className="overflow-x-auto rounded-xl border border-theme bg-theme-surface/50">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-theme bg-theme-surface text-theme font-semibold">
                  <tr>
                    <th className="py-3 px-4">Capability</th>
                    <th className="py-3 px-4">Solarch</th>
                    <th className="py-3 px-4">PocketBase</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme text-theme-secondary">
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Primary Server Language</td>
                    <td className="py-3 px-4">TypeScript / JavaScript</td>
                    <td className="py-3 px-4">Go</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Server Runtime</td>
                    <td className="py-3 px-4">Node.js (20+ LTS)</td>
                    <td className="py-3 px-4">Compiled Go binary</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Database Engine</td>
                    <td className="py-3 px-4">Embedded SQLite via <code className="text-xs text-primary">better-sqlite3</code> (WAL mode)</td>
                    <td className="py-3 px-4">Embedded SQLite via Go SQLite driver (WAL mode)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Database Layout</td>
                    <td className="py-3 px-4">Dual SQLite files (<code className="text-xs">data.db</code>, <code className="text-xs">auxiliary.db</code>)</td>
                    <td className="py-3 px-4">Dual SQLite files (<code className="text-xs">data.db</code>, <code className="text-xs">auxiliary.db</code>)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Realtime Transport</td>
                    <td className="py-3 px-4">Bidirectional WebSocket + Server-Sent Events (SSE)</td>
                    <td className="py-3 px-4">Server-Sent Events (SSE)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Backend Extension Model</td>
                    <td className="py-3 px-4">Native Express routes/middleware, npm packages, isolated Node VM hooks</td>
                    <td className="py-3 px-4">Go framework compilation or embedded Goja ECMAScript 5.1+ JS hooks</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Distribution &amp; Packaging</td>
                    <td className="py-3 px-4">npm package (<code className="text-xs">solarch</code>), programmatic Express embedding, Docker</td>
                    <td className="py-3 px-4">Single prebuilt standalone executable, Go package (<code className="text-xs">pocketbase</code>), Docker</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Storage Drivers</td>
                    <td className="py-3 px-4">Local disk, S3-compatible (AWS S3, Cloudflare R2, MinIO, DO Spaces) with on-the-fly Sharp resizing</td>
                    <td className="py-3 px-4">Local disk, S3-compatible cloud storage</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Authentication</td>
                    <td className="py-3 px-4">Email/password, OAuth2, Passwordless OTP, TOTP/MFA, JWT</td>
                    <td className="py-3 px-4">Email/password, OAuth2, Username, JWT</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Admin Dashboard</td>
                    <td className="py-3 px-4">Embedded React-based Admin UI</td>
                    <td className="py-3 px-4">Embedded Svelte-based Admin UI</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-theme">Open-Source License</td>
                    <td className="py-3 px-4">Apache-2.0</td>
                    <td className="py-3 px-4">MIT</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Core Architecture */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Core Architecture: Node.js Runtime vs. Compiled Go Executable
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              The central distinction between Solarch and PocketBase lies in the server execution environment.
            </p>
            <p className="text-base leading-relaxed text-theme-secondary">
              <strong>PocketBase</strong> is authored entirely in Go. In its default distribution, it is packaged
              as a single precompiled executable file. You download the binary, run{' '}
              <code className="rounded bg-theme-surface px-1.5 py-0.5 text-xs font-mono text-primary">./pocketbase serve</code>,
              and your HTTP server, SQLite connection, and Svelte admin UI are immediately operational. There is
              no runtime dependency on Node.js, Python, or external shared libraries.
            </p>
            <p className="text-base leading-relaxed text-theme-secondary">
              <strong>Solarch</strong> is authored entirely in TypeScript for the Node.js runtime. It is published
              as a standard npm package. You can launch it via the CLI with{' '}
              <code className="rounded bg-theme-surface px-1.5 py-0.5 text-xs font-mono text-primary">npx solarch serve</code>{' '}
              or embed it programmatically directly inside an existing Node.js or Express application via{' '}
              <code className="rounded bg-theme-surface px-1.5 py-0.5 text-xs font-mono text-primary">import &#123; createServer &#125; from 'solarch'</code>.
              Learn more about this architecture in the{' '}
              <Link to="/docs/overview/introduction" className="text-primary underline hover:text-brand-cyan transition-colors">
                Solarch Architecture Introduction
              </Link>.
            </p>
          </section>

          {/* TypeScript / Node.js Integration */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              TypeScript &amp; Node.js Integration
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              When comparing TypeScript integration, it is essential to distinguish between the{' '}
              <em>client SDK language</em> and the <em>server runtime / extension language</em>.
            </p>
            <div className="rounded-xl border border-theme bg-theme-surface p-4 space-y-3">
              <h3 className="text-sm font-semibold text-theme">Client SDK vs. Server Runtime</h3>
              <ul className="space-y-2 text-sm text-theme-secondary">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>Both platforms offer TypeScript client SDKs:</strong> Both PocketBase and Solarch
                    provide official TypeScript libraries that frontend web applications (React, Vue, Svelte,
                    Next.js) can use to authenticate, query records, and subscribe to data changes.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>Only Solarch is TypeScript-native on the server:</strong> With Solarch, your backend
                    controllers, middleware, lifecycle hooks, and server integrations execute in the native Node.js
                    JavaScript/TypeScript runtime. Type definitions can be shared directly across full-stack
                    repositories without crossing runtime barriers.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Realtime */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Realtime Transport: WebSocket + SSE vs. PocketBase SSE
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              Realtime functionality is critical for reactive applications, but the two platforms approach
              network transport differently:
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-theme bg-theme-surface/40 p-4 space-y-2">
                <h3 className="font-semibold text-theme text-sm">PocketBase Realtime</h3>
                <p className="text-xs text-theme-secondary leading-relaxed">
                  PocketBase utilizes <strong>Server-Sent Events (SSE)</strong> over HTTP for its realtime API.
                  SSE is simple, efficient, and operates over standard HTTP/1.1 or HTTP/2 connections. It is
                  unidirectional: the client receives server-pushed updates over the SSE stream, while client-to-server
                  mutations are dispatched as standard HTTP POST, PATCH, or DELETE requests.
                </p>
              </div>
              <div className="rounded-xl border border-theme bg-theme-surface/40 p-4 space-y-2">
                <h3 className="font-semibold text-theme text-sm">Solarch Realtime</h3>
                <p className="text-xs text-theme-secondary leading-relaxed">
                  Solarch supports <strong>bidirectional WebSockets</strong> alongside <strong>Server-Sent Events</strong>.
                  WebSockets maintain a persistent, full-duplex TCP connection, enabling client-to-server and
                  server-to-client messaging with topic-based channels, connection lifecycle tracking, and
                  automatic disconnect cleanup. See the{' '}
                  <Link to="/docs/realtime/websocket" className="text-primary underline hover:text-brand-cyan transition-colors">
                    WebSocket Realtime Guide
                  </Link>.
                </p>
              </div>
            </div>
            <p className="text-sm text-theme-secondary leading-relaxed">
              <strong>Trade-off:</strong> SSE is lightweight and requires no custom WebSocket protocol framing,
              making it well suited for dashboards, notification feeds, and straightforward subscription models.
              Full-duplex WebSockets provide lower-latency bidirectional messaging, which is advantageous for
              high-frequency interactive applications like collaborative document editors, live bidding rooms,
              and multiplayer interfaces.
            </p>
          </section>

          {/* SQLite and Data Model */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              SQLite and the Data Layer
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              Both platforms share the design philosophy that embedded SQLite is robust, fast, and operationally
              simpler than managing external database clusters.
            </p>
            <ul className="space-y-2 text-sm text-theme-secondary">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong>Concurrency &amp; WAL:</strong> Both engines configure SQLite in Write-Ahead Logging
                  (WAL) mode. WAL allows concurrent readers to proceed without blocking writers, and writers
                  to write without blocking readers. Both employ busy timeouts to handle brief lock contention.
                  Review Solarch's configuration in the{' '}
                  <Link to="/docs/data-services/database" className="text-primary underline hover:text-brand-cyan transition-colors">
                    SQLite Database Guide
                  </Link>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong>Dual-Database Architecture:</strong> Both platforms isolate application data from
                  internal system state. User collections reside in <code className="text-xs">data.db</code>,
                  while application settings, logs, and internal configuration reside in{' '}
                  <code className="text-xs">auxiliary.db</code>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong>Driver Implementation:</strong> PocketBase interacts with SQLite via Go's database driver
                  interfaces. Solarch utilizes <code className="text-xs">better-sqlite3</code>, the fastest synchronous
                  Node.js SQLite binding, enabling direct in-process query execution without IPC latency.
                </span>
              </li>
            </ul>
          </section>

          {/* Extending the Backend */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Extending the Backend: Go vs. Node.js
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              Every production backend eventually requires custom business logic, third-party API calls, or
              custom endpoints. The two platforms take very different approaches to extensibility:
            </p>
            <div className="space-y-4">
              <div className="rounded-xl border border-theme bg-theme-surface p-4 space-y-2">
                <h3 className="text-base font-semibold text-theme">PocketBase Extension Mechanisms</h3>
                <p className="text-sm text-theme-secondary leading-relaxed">
                  PocketBase offers two primary extension paths:
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-theme-secondary pl-1">
                  <li>
                    <strong>Go Framework:</strong> You can import PocketBase as a Go package into your own{' '}
                    <code className="text-xs">main.go</code>, register event hooks, attach custom Go HTTP handlers,
                    and compile a tailored binary. This provides maximum performance and full access to the Go ecosystem.
                  </li>
                  <li>
                    <strong>JavaScript Hooks (Goja):</strong> PocketBase embeds the <code className="text-xs">goja</code>{' '}
                    ECMAScript 5.1+ interpreter. You place <code className="text-xs">*.pb.js</code> files in{' '}
                    <code className="text-xs">pb_hooks/</code> to intercept record events. Because Goja is an embedded
                    pure-Go interpreter and not Node.js, you cannot install arbitrary npm packages with native bindings,
                    and modern Node.js standard libraries are not available.
                  </li>
                </ol>
              </div>

              <div className="rounded-xl border border-theme bg-theme-surface p-4 space-y-2">
                <h3 className="text-base font-semibold text-theme">Solarch Extension Mechanisms</h3>
                <p className="text-sm text-theme-secondary leading-relaxed">
                  Solarch operates directly inside the native Node.js runtime:
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-theme-secondary pl-1">
                  <li>
                    <strong>Direct Express &amp; npm Integration:</strong> Solarch's HTTP layer is built on Express.
                    You can register standard Express middleware, install any npm library (Stripe, OpenAI, Zod,
                    Resend), and define custom route handlers directly.
                  </li>
                  <li>
                    <strong>Sandboxed Lifecycle Hooks:</strong> For dynamic hooks, Solarch executes JavaScript in an
                    isolated Node.js VM sandbox with access to global utilities (<code className="text-xs">$app</code>,{' '}
                    <code className="text-xs">$db</code>, <code className="text-xs">$security</code>). See the{' '}
                    <Link to="/docs/javascript-hooks/available-globals" className="text-primary underline hover:text-brand-cyan transition-colors">
                      JavaScript Hooks Reference
                    </Link>.
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Deployment and Operational Models */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              Deployment and Operational Models
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              Both systems avoid multi-container cluster overhead, but their packaging models reflect their
              respective runtime ecosystems:
            </p>
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <div className="rounded-xl border border-theme bg-theme-surface/40 p-4 space-y-2">
                <h3 className="font-semibold text-theme">Deploying PocketBase</h3>
                <p className="text-theme-secondary text-xs leading-relaxed">
                  PocketBase is distributed as a single static binary. Deploying to a Linux VPS or container host
                  typically requires copying the single binary file, setting execute permissions, and running it
                  under systemd or Docker. There are no runtime package managers or Node.js versions to configure.
                </p>
              </div>
              <div className="rounded-xl border border-theme bg-theme-surface/40 p-4 space-y-2">
                <h3 className="font-semibold text-theme">Deploying Solarch</h3>
                <p className="text-theme-secondary text-xs leading-relaxed">
                  Solarch is installed via npm or Docker. It runs anywhere Node.js 20+ LTS is supported—including VPS
                  instances, Fly.io, Railway, Render, or Docker containers—often managed via PM2 or systemd.
                  Review step-by-step instructions in the{' '}
                  <Link to="/docs/hosting-deployment/deployment" className="text-primary underline hover:text-brand-cyan transition-colors">
                    Production Deployment Guide
                  </Link>.
                </p>
              </div>
            </div>
          </section>

          {/* When to Choose Solarch */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              When Solarch is a Good Fit
            </h2>
            <div className="rounded-xl border border-theme bg-theme-surface p-5 space-y-3">
              <ul className="space-y-2.5 text-sm text-theme-secondary">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>Your team works primarily in TypeScript/JavaScript:</strong> You want a backend
                    where schema definitions, custom business logic, and API clients share types naturally without
                    switching between Go and TypeScript.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You need direct access to the npm ecosystem:</strong> You plan to use npm libraries
                    (AI SDKs, payment processors, custom parsers, validation tools) inside your backend without
                    building custom binary wrappers.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You require bidirectional WebSockets:</strong> Your application involves collaborative
                    editing, live bidding, multiplayer state, or interactive protocols that benefit from persistent
                    full-duplex connections.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You want an embeddable backend in Node.js:</strong> You have an existing Express application
                    or Node service and want to mount BaaS collections and admin tooling directly into your existing
                    process.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You need built-in TOTP / Multi-Factor Authentication:</strong> Solarch includes native
                    TOTP multi-factor authentication out of the box. See the{' '}
                    <Link to="/docs/security-auth/authentication" className="text-primary underline hover:text-brand-cyan transition-colors">
                      Authentication Reference
                    </Link>.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* When to Choose PocketBase */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              When PocketBase is a Good Fit
            </h2>
            <div className="rounded-xl border border-theme bg-theme-surface p-5 space-y-3">
              <ul className="space-y-2.5 text-sm text-theme-secondary">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You prefer a single, self-contained executable:</strong> You want zero runtime
                    dependencies—no Node.js runtime, no node_modules folder, and no container build steps required
                    for simple deployments.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You write or prefer Go:</strong> If your engineering team is proficient in Go,
                    extending PocketBase as a Go framework provides compiled speed, static typing, and direct
                    access to Go packages.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>SSE realtime meets your functional needs:</strong> Your application primarily requires
                    server-to-client notifications or record change streams rather than bidirectional socket channels.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>You want an established, mature community:</strong> PocketBase has been in active open-source
                    development for multiple years with extensive community plugins, third-party SDKs, and community
                    guides.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Bottom Line */}
          <section className="rounded-2xl border border-theme bg-theme-surface/50 p-6 sm:p-8 space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-theme">
              The Bottom Line
            </h2>
            <p className="text-base leading-relaxed text-theme-secondary">
              PocketBase is an exceptional tool for developers who appreciate Go's single-binary distribution,
              minimalist deployment, and battle-tested embedded SQLite design.
            </p>
            <p className="text-base leading-relaxed text-theme-secondary">
              Solarch applies that same embedded SQLite and single-process philosophy to the TypeScript and
              Node.js ecosystem. If you want a Backend-as-a-Service that integrates natively with modern
              TypeScript tooling, Express middleware, bidirectional WebSockets, and the vast npm ecosystem,
              Solarch was built for your stack.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/docs/overview/getting-started"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:brightness-110 transition-all"
              >
                <span>Get Started with Solarch</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 rounded-full border border-theme bg-theme-surface px-5 py-2.5 text-sm font-semibold text-theme hover:bg-theme-muted transition-all"
              >
                <span>Explore FAQ</span>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  )
}
